import { z } from 'zod';
import {
  AU_STATE_MAPPINGS,
  CA_PROVINCE_MAPPINGS,
  IE_COUNTY_MAPPINGS,
  JP_PREFECTURE_MAPPINGS,
  US_STATE_MAPPINGS,
} from 'constants/mappings';
import {
  FIRST_NAME_SCHEMA,
  LAST_NAME_SCHEMA,
  COMPANY_SCHEMA,
  ADDRESS_LINE_1_SCHEMA,
  ADDRESS_LINE_2_SCHEMA,
  LOCALITY_SCHEMA,
  PHONE_SCHEMA,
  POSTAL_CODE_SCHEMA,
  EMAIL_SCHEMA,
} from 'typescript/schemas/address_forms/common';
import { CountryCode } from 'typescript/constants/localization';

/**
 * The way we'll be using these schemas along with our forms is that
 * the schema will be passed to the zodResolver function from the @hookform/resolvers/zod package
 *
 * a key in the schema object = name of the field in the form
 * so first_name key will validate the  controlled input with
 * the attribute name="first_name" in the form
 *
 * for billing address, we'll prefix the keys with 'billing_'
 */

export enum AddressType {
  shipping = 'shipping',
  billing = 'billing',
}

export const BILLING_PREFIX = 'billing_';
export const ADDRESS_FIELDS = {
  first_name: 'first_name',
  last_name: 'last_name',
  company: 'company',
  address_line_1: 'address_line_1',
  address_line_2: 'address_line_2',
  locality: 'locality',
  postal_code: 'postal_code',
  phone: 'phone',
  email: 'email',
  administrative_district_level_1: 'administrative_district_level_1',
};

export const getAddressPrefix = (addressType: AddressType): string =>
  addressType === AddressType.billing ? BILLING_PREFIX : '';

/**
 * schema for administrative district level 1 field
 * this field is only required for certain countries
 * ES and FR do not require this field
 * US, JP, IE, GB, CA, AU require this field
 */
export const administrativeDistrictSchemas = {
  [CountryCode.US]: z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .refine((value) => value in US_STATE_MAPPINGS, {
      message: 'error.validation.invalidFieldWithLabel',
    }),
  [CountryCode.JP]: z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .refine((value) => value in JP_PREFECTURE_MAPPINGS, {
      message: 'error.validation.invalidFieldWithLabel',
    }),
  [CountryCode.IE]: z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .refine((value) => value in IE_COUNTY_MAPPINGS, {
      message: 'error.validation.invalidFieldWithLabel',
    }),
  [CountryCode.GB]: z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .max(40, { message: 'error.validation.invalidFieldWithLabel' }),
  [CountryCode.CA]: z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .refine((value) => value in CA_PROVINCE_MAPPINGS, {
      message: 'error.validation.invalidFieldWithLabel',
    }),
  [CountryCode.AU]: z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .refine((value) => value in AU_STATE_MAPPINGS, {
      message: 'error.validation.invalidFieldWithLabel',
    }),
};

/**
 * generates the schema for the address fields based on the country
 * @param addressType billing or shipping, billing will prefix the keys with 'billing_'
 * @param countryCode country code of current store
 * @returns address fields schema to be used in the zodResolver in react-hook-form
 */
export const createAddressFieldsSchema = ({
  addressType,
  countryCode,
}: {
  addressType: AddressType;
  countryCode: CountryCode;
}): z.ZodObject<any> => {
  const prefix = getAddressPrefix(addressType);
  const addressSchema = {
    [`${prefix}${ADDRESS_FIELDS.first_name}`]: FIRST_NAME_SCHEMA,
    [`${prefix}${ADDRESS_FIELDS.last_name}`]: LAST_NAME_SCHEMA,
    [`${prefix}${ADDRESS_FIELDS.company}`]: COMPANY_SCHEMA,
    [`${prefix}${ADDRESS_FIELDS.address_line_1}`]: ADDRESS_LINE_1_SCHEMA,
    [`${prefix}${ADDRESS_FIELDS.address_line_2}`]: ADDRESS_LINE_2_SCHEMA,
    [`${prefix}${ADDRESS_FIELDS.locality}`]: LOCALITY_SCHEMA,
    [`${prefix}${ADDRESS_FIELDS.postal_code}`]: POSTAL_CODE_SCHEMA(countryCode),
    [`${prefix}${ADDRESS_FIELDS.phone}`]: PHONE_SCHEMA(countryCode),
  };

  if (addressType === AddressType.shipping) {
    addressSchema[`${prefix}${ADDRESS_FIELDS.email}`] = EMAIL_SCHEMA;
  }

  if (countryCode in administrativeDistrictSchemas) {
    addressSchema[
      `${prefix}${ADDRESS_FIELDS.administrative_district_level_1}`
    ] = administrativeDistrictSchemas[countryCode];
  }

  return z.object(addressSchema);
};
